import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const BulkReassignDialog = ({
  open, onOpenChange,
  selectedPhotos, students,
  saving, onApply,
}) => {
  const { t } = useLanguage();

  // The student-picker only makes sense if every selected photo belongs to the same class.
  const sharedClassId = useMemo(() => {
    if (selectedPhotos.length === 0) return null;
    const first = selectedPhotos[0].class_id;
    return selectedPhotos.every((p) => p.class_id === first) ? first : null;
  }, [selectedPhotos]);

  // Default the radio to "assign to student" when it's a valid choice
  // (i.e. all selected photos share a class). If they don't share a class,
  // "student" would be disabled, so fall back to class_photo.
  const [mode, setMode] = useState(() => (sharedClassId ? 'student' : 'class_photo'));
  const [studentId, setStudentId] = useState('');

  const availableStudents = useMemo(
    () => (sharedClassId ? students.filter((s) => s.class_id === sharedClassId) : []),
    [students, sharedClassId],
  );

  useEffect(() => {
    if (open) {
      setMode(sharedClassId ? 'student' : 'class_photo');
      setStudentId('');
    }
  }, [open, sharedClassId]);

  const handleApply = () => {
    if (mode === 'class_photo') {
      onApply({ is_class_photo: true, student_ids: [] });
    } else if (mode === 'student') {
      if (!studentId) return;
      onApply({ is_class_photo: false, student_ids: [studentId] });
    } else if (mode === 'clear') {
      onApply({ clear_assignment: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin.bulkReassignDialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('admin.bulkReassignDialogDesc').replace('{n}', selectedPhotos.length)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <RadioGroup value={mode} onValueChange={setMode}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="class_photo" id="bulk-mode-class" data-testid="bulk-reassign-mode-class" />
              <Label htmlFor="bulk-mode-class" className="cursor-pointer">
                {t('admin.bulkReassignAsClassPhoto')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="student"
                id="bulk-mode-student"
                disabled={!sharedClassId}
                data-testid="bulk-reassign-mode-student"
              />
              <Label
                htmlFor="bulk-mode-student"
                className={`cursor-pointer ${!sharedClassId ? 'text-muted-foreground' : ''}`}
              >
                {t('admin.bulkReassignToStudent')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="clear" id="bulk-mode-clear" data-testid="bulk-reassign-mode-clear" />
              <Label htmlFor="bulk-mode-clear" className="cursor-pointer">
                {t('admin.bulkReassignClear')}
              </Label>
            </div>
          </RadioGroup>

          {!sharedClassId && (
            <p className="text-xs text-amber-500">{t('admin.bulkMultipleClassesWarning')}</p>
          )}

          {mode === 'student' && sharedClassId && (
            <div className="space-y-2">
              <Label>{t('admin.selectStudent')}</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="bg-secondary border-border" data-testid="bulk-reassign-student-select">
                  <SelectValue placeholder={t('admin.selectStudent')} />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {availableStudents.map((s) => (
                    <SelectItem key={s.student_id} value={s.student_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleApply}
              disabled={saving || (mode === 'student' && !studentId)}
              className="bg-primary hover:bg-primary/90"
              data-testid="bulk-reassign-apply"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('admin.bulkApply')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
